/**
 * Registre des règles générales d'administration.
 *
 * Une règle globale = une entrée déclarative ici + une clé namespacée dans le
 * sac jsonb `company_settings.rules` côté serveur. L'écran d'administration
 * (`GeneralRulesPanel`) se génère depuis ce registre : ajouter une règle
 * booléenne ne demande aucun composant supplémentaire.
 */

/** Onglets de l'écran d'administration, dans l'ordre d'affichage. */
export const RULE_DOMAINS = [
  { key: 'web', label: 'Sites web' },
  { key: 'privacy', label: 'Données personnelles' },
  // { key: 'proposal', label: 'Documents' },  ← à venir
];

/** Clé de la règle qui verrouille la publication d'un site web. */
export const WEB_PUBLICATION_VALIDATION_RULE = 'web.publication_requires_admin_validation';

/** Clé de la règle listant les contenus du document masqués sur le site. */
export const WEB_HIDDEN_PROPOSAL_BOX_TYPES = 'web.hidden_proposal_box_types';

/** Clé de la règle réduisant une séparation de chapitre à son seul titre. */
export const WEB_CHAPTER_SEPARATION_TITLE_ONLY = 'web.chapter_separation_title_only';

/** Clé de la règle autorisant la modification du squelette depuis l'éditeur. */
export const WEB_ALLOW_SKELETON_EDIT = 'web.allow_skeleton_edit';

/** Clé de la règle autorisant la saisie d'une couleur hors charte dans l'éditeur de site. */
export const WEB_FREE_COLORS = 'web.free_colors';

/**
 * Contact « données personnelles » montré aux lecteurs des sites publiés
 * (Proposals::PrivacyNotice, dépôt web). Vides par défaut : le nom, l'adresse
 * et l'e-mail de l'entité du document suffisent légalement (RGPD art. 13.1.a).
 */
export const PRIVACY_CONTACT_EMAIL = 'privacy.contact_email';

/**
 * Le suivi de lecture se décide par la société, responsable du traitement :
 * désactivé tant qu'elle ne l'a pas activé (Proposals::PrivacyNotice, dépôt web).
 */
export const PRIVACY_READING_TRACKING_ENABLED = 'privacy.reading_tracking_enabled';
export const PRIVACY_DPO_NAME = 'privacy.dpo_name';
export const PRIVACY_DPO_EMAIL = 'privacy.dpo_email';
export const PRIVACY_POLICY_URL = 'privacy.policy_url';

/**
 * Cases de la règle « contenus masqués ». Une case peut couvrir plusieurs types
 * de box du Proposal : `types` porte la liste, pour que l'utilisateur règle une
 * intention (« les cases à cocher ») et non une constante interne.
 */
export const HIDEABLE_PROPOSAL_BOX_TYPES = [
  {
    value: 'checkboxs_variable',
    label: 'Cases à cocher',
    // `question` est rendu par le même composant côté Proposal (Box.jsx) :
    // le distinguer ici n'aurait aucun sens pour qui règle son site.
    types: ['checkboxs_variable', 'question'],
  },
  {
    value: 'selector_variable',
    label: 'Choix',
    types: ['selector_variable'],
  },
];

export const GLOBAL_RULES = [
  {
    key: WEB_PUBLICATION_VALIDATION_RULE,
    domain: 'web',
    type: 'boolean',
    default: false,
    label: 'Validation admin avant publication',
    help: "Tant qu'un administrateur n'a pas validé le site d'un document, "
      + 'les autres utilisateurs ne peuvent pas le publier.',
  },
  {
    key: WEB_HIDDEN_PROPOSAL_BOX_TYPES,
    domain: 'web',
    type: 'boxTypes',
    default: ['checkboxs_variable', 'selector_variable'],
    options: HIDEABLE_PROPOSAL_BOX_TYPES,
    label: 'Contenus du document masqués sur le site',
    help: "Ces contenus restent dans le document ; ils ne sont simplement pas "
      + 'repris sur le site. Le réglage s\'applique aux sites générés ou '
      + 'régénérés à partir de maintenant, pas aux sites existants.',
  },
  {
    key: WEB_CHAPTER_SEPARATION_TITLE_ONLY,
    domain: 'web',
    type: 'boolean',
    default: true,
    label: 'Séparations de chapitre réduites à leur titre',
    help: 'Une page de séparation de chapitre rend mal sur un site : seul son '
      + "titre est repris. S'applique aux sites générés ou régénérés à partir "
      + 'de maintenant.',
  },
  {
    key: WEB_ALLOW_SKELETON_EDIT,
    domain: 'web',
    type: 'boolean',
    default: false,
    label: 'Modification du squelette depuis l\'éditeur de site',
    help: 'Autorise chacun à modifier le menu, le pied de page, la page de '
      + 'connexion et la page de chargement de ses sites. Les administrateurs '
      + 'y ont toujours accès.',
  },
  {
    key: WEB_FREE_COLORS,
    domain: 'web',
    type: 'boolean',
    default: true,
    label: 'Couleurs libres dans l\'éditeur de site',
    help: 'Autorise la saisie d\'une couleur hors de la charte. Désactivée, '
      + 'chacun choisit parmi les couleurs de la charte ; celles déjà posées '
      + 'sur un site restent en place.',
  },
  {
    key: PRIVACY_READING_TRACKING_ENABLED,
    domain: 'privacy',
    type: 'boolean',
    default: false,
    label: 'Proposer le suivi de lecture aux destinataires de vos sites',
    help: 'Chaque destinataire peut l\'accepter ou le refuser. Votre société est '
      + 'responsable de ce suivi : activez-le après avoir adopté l\'analyse '
      + 'd\'impact (AIPD) fournie avec l\'outil. Il faut aussi un e-mail ou une '
      + 'adresse de contact.',
  },
  {
    key: PRIVACY_CONTACT_EMAIL,
    domain: 'privacy',
    type: 'text',
    inputType: 'email',
    default: '',
    label: 'E-mail pour les questions sur les données',
    help: 'Montré aux destinataires de vos sites pour exercer leurs droits. Vide : '
      + "l'e-mail de l'entité du document. Sans e-mail ni adresse, le suivi de "
      + "lecture n'est pas proposé aux destinataires.",
  },
  {
    key: PRIVACY_DPO_NAME,
    domain: 'privacy',
    type: 'text',
    inputType: 'text',
    default: '',
    label: 'Délégué à la protection des données (nom)',
    help: 'À renseigner si votre société en a désigné un : il doit alors être '
      + 'indiqué aux destinataires.',
  },
  {
    key: PRIVACY_DPO_EMAIL,
    domain: 'privacy',
    type: 'text',
    inputType: 'email',
    default: '',
    label: 'Délégué à la protection des données (e-mail)',
    help: '',
  },
  {
    key: PRIVACY_POLICY_URL,
    domain: 'privacy',
    type: 'text',
    inputType: 'url',
    default: '',
    label: 'Politique de confidentialité de votre société',
    help: 'Adresse https:// ; un lien y renvoie depuis la notice « Vos données » '
      + 'de vos sites.',
  },
];

/** Valeur d'une règle dans un sac de règles, repli sur le défaut du registre.
 *  Le serveur ne renvoie que les règles explicitement posées. */
export function getRuleValue(rules, key) {
  const definition = GLOBAL_RULES.find((rule) => rule.key === key);
  const fallback = definition ? definition.default : undefined;
  const value = rules ? rules[key] : undefined;
  return value === undefined || value === null ? fallback : value;
}

/** Règles d'un domaine, dans l'ordre du registre. */
export function getRulesForDomain(domainKey) {
  return GLOBAL_RULES.filter((rule) => rule.domain === domainKey);
}

/**
 * Types de box du Proposal à ne pas reprendre sur le site, dépliés depuis les
 * cases cochées. Consommé par le parser à la génération.
 */
export function resolveHiddenProposalBoxTypes(rules) {
  const checked = getRuleValue(rules, WEB_HIDDEN_PROPOSAL_BOX_TYPES) || [];
  return new Set(
    HIDEABLE_PROPOSAL_BOX_TYPES
      .filter((option) => checked.includes(option.value))
      .flatMap((option) => option.types),
  );
}
